import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Icon } from "@mui/material";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Context
import { useMaterialUIController } from "context";

// Utils
import { formatLocalDateTime, formatLocalDate, formatLocalTime } from "utils/dateFormatter";

// Components
import { GenericListScreen, Column } from "components/GenericListScreen";
import AddUserDialog from "./components/AddUserDialog/AddUserDialog";
import UserDetailDialog from "./components/UserDetailDialog/UserDetailDialog";

// Constants
const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";

function MediInStepUserScreen() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // React Query Client
  const queryClient = useQueryClient();

  // Dialog States
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [userDetailDialogOpen, setUserDetailDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Tenant ID from storage override for API params
  const tenantId = useMemo(() => {
    const savedCallback = localStorage.getItem(SELECTED_TENANT_KEY);
    return savedCallback ? parseInt(savedCallback, 10) : undefined;
  }, []);

  const extraParams = useMemo(() => ({
    TenantSeqId: tenantId
  }), [tenantId]);

  // Handlers
  const handleRowClick = (row: any) => {
    setSelectedUserId(row.id);
    setUserDetailDialogOpen(true);
  };

  const handleAddUserSuccess = () => {
    // Invalidate the cache to trigger a refresh
    queryClient.invalidateQueries({ queryKey: ["User.pagedList"] });
    setAddUserDialogOpen(false);
  };

  // Render Date Cell Helper
  const renderDateCell = (value: string | null) => {
    if (!value || value === "-") return "-";

    return (
      <MDBox lineHeight={1.2} textAlign="center">
        <MDTypography variant="caption" display="block" color="text" fontWeight="medium">
          {formatLocalDate(value)}
        </MDTypography>
        <MDTypography variant="caption" display="block" color="text">
          {formatLocalTime(value)}
        </MDTypography>
      </MDBox>
    );
  };

  const columns: Column[] = [
    {
      Header: "User",
      accessor: "name",
      align: "left",
      width: "20%",
      Cell: ({ row }: any) => (
        <MDBox display="flex" alignItems="center">
          <Avatar
            sx={{
              width: 36,
              height: 36,
              mr: 1.5,
              bgcolor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
              color: darkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
              fontWeight: "bold",
              fontSize: "0.875rem",
            }}
          >
            {row.original.name ? row.original.name.charAt(0).toUpperCase() : "?"}
          </Avatar>
          <MDTypography variant="button" fontWeight="medium">
            {row.original.name}
          </MDTypography>
        </MDBox>
      )
    },
    { Header: "Email", accessor: "email", align: "left", width: "18%" },
    { Header: "Phone", accessor: "phone", align: "left", width: "13%" },
    { Header: "Role", accessor: "role", align: "center", width: "8%" },
    {
      Header: "App Ver",
      accessor: "appVersion",
      align: "center",
      width: "8%",
      Cell: ({ value }: any) => (
        <MDTypography variant="caption" fontWeight="medium" color={value ? "text" : "secondary"}>
          {value || "-"}
        </MDTypography>
      )
    },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      width: "10%",
      Cell: ({ value }: any) => (
        <Chip
          label={value ? "활성" : "비활성"}
          color={value ? "success" : "error"}
          size="small"
          sx={{
            fontWeight: "medium",
            minWidth: 60,
            ...(value && {
              backgroundColor: "#4CAF50",
              color: "#ffffff",
              "& .MuiChip-label": {
                color: "#ffffff",
              },
            }),
          }}
        />
      )
    },
    {
      Header: "Created",
      accessor: "created",
      align: "center",
      width: "15%",
      Cell: ({ value }: any) => renderDateCell(value)
    },
    { Header: "ID", accessor: "id", align: "left", width: "10%" },
  ];

  const dataTransform = (data: any) => {
    const items = (data.items || []).map((user: any) => {
      const isActive = user.isActive !== undefined ? user.isActive : user.enable;
      return {
        id: user.id,
        name: user.name || "-",
        email: user.email || "-",
        phone: user.mobile || "-",
        role: user.tenantRole || user.role || "USER",
        appVersion: user.appVersion || null,
        status: isActive,
        created: user.crtDt || null,
        // Keep original for avatar logic if needed, though we mapped name/crtDt above
        original: user
      };
    });
    return { items, totalCount: data.totalCount || 0 };
  };

  return (
    <>
      <GenericListScreen
        title="사용자 리스트 ( USER LIST )"
        apiName="User.pagedList"
        columns={columns}
        dataTransform={dataTransform}
        extraParams={extraParams}
        onRowClick={handleRowClick}
        actionComponent={
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => setAddUserDialogOpen(true)}
          >
            <Icon fontSize="small" sx={{ mr: 0.5 }}>add</Icon>
            사용자 추가
          </MDButton>
        }
      />

      {/* Dialogs */}
      <AddUserDialog
        open={addUserDialogOpen}
        onClose={() => setAddUserDialogOpen(false)}
        onSuccess={handleAddUserSuccess}
        darkMode={darkMode}
      />
      <UserDetailDialog
        open={userDetailDialogOpen}
        onClose={() => {
          setUserDetailDialogOpen(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdateSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["User.pagedList"] });
        }}
      />
    </>
  );
}

export default MediInStepUserScreen;
